import { useState, useEffect } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, RotateCcw, RefreshCw, Users, ShieldCheck, Send, Sparkles, Clock, CalendarPlus, UserCheck, X, ExternalLink, Database } from "lucide-react";
import { PiiAuditCard } from "@/components/PiiAuditCard";
import { ImportReconcileCard } from "@/components/ImportReconcileCard";
import { CommunicationsCard } from "@/components/CommunicationsCard";
import { AssistantCohortCard } from "@/components/AssistantCohortCard";
import CacheCoverageDashboard from "@/components/CacheCoverageDashboard";

export default function AdminPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [form, setForm] = useState({ username: "", email: "", role: "user", password: "" });
  const [waitlist, setWaitlist] = useState({ waitlist: [], count: 0 });
  const [clerkWaitlist, setClerkWaitlist] = useState({
    entries: [], count: 0, clerk_dashboard_url: "https://dashboard.clerk.com", error: null,
  });
  const [clerkActingId, setClerkActingId] = useState(null);
  const [announcing, setAnnouncing] = useState(false);
  const [trialing, setTrialing] = useState({ trialing: [], count: 0 });
  const [extendingUid, setExtendingUid] = useState(null);
  const [houseProvider, setHouseProvider] = useState(null);
  const [savingProvider, setSavingProvider] = useState(false);
  const [runningRefresh, setRunningRefresh] = useState(false);
  const [runningIntel, setRunningIntel] = useState(false);

  const loadUsers = async () => {
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data.users || []);
    } catch (err) {
      console.error("Admin users error:", err);
    } finally {
      setLoading(false);
    }
  };

  const loadClerkWaitlist = async () => {
    try {
      const { data } = await api.get("/admin/clerk-waitlist");
      setClerkWaitlist({
        entries: data.entries || [],
        count: data.count || 0,
        clerk_dashboard_url: data.clerk_dashboard_url || "https://dashboard.clerk.com",
        error: data.error || null,
      });
    } catch (err) {
      console.error("Clerk waitlist error:", err);
      setClerkWaitlist((prev) => ({
        ...prev,
        entries: [],
        count: 0,
        error: err.response?.data?.detail?.error || err.response?.data?.detail || "Could not load Clerk waitlist",
        clerk_dashboard_url: err.response?.data?.detail?.clerk_dashboard_url || prev.clerk_dashboard_url,
      }));
    }
  };

  const handleClerkInvite = async (id, email) => {
    if (!window.confirm(`Invite ${email}? Clerk will send the branded Invitation email.`)) return;
    setClerkActingId(id);
    try {
      await api.post(`/admin/clerk-waitlist/${id}/invite`);
      await loadClerkWaitlist();
    } catch (err) {
      console.error("Clerk invite error:", err);
      alert(formatApiError(err.response?.data?.detail) || "Invite failed");
    } finally {
      setClerkActingId(null);
    }
  };

  const handleClerkReject = async (id, email) => {
    if (!window.confirm(`Deny ${email}? This rejects the Clerk waitlist entry.`)) return;
    setClerkActingId(id);
    try {
      await api.post(`/admin/clerk-waitlist/${id}/reject`);
      await loadClerkWaitlist();
    } catch (err) {
      console.error("Clerk reject error:", err);
      alert(formatApiError(err.response?.data?.detail) || "Deny failed");
    } finally {
      setClerkActingId(null);
    }
  };

  const loadWaitlist = async () => {
    try {
      const { data } = await api.get("/admin/tier/waitlist");
      setWaitlist(data || { waitlist: [], count: 0 });
    } catch (err) {
      console.error("Waitlist error:", err);
    }
  };

  const loadHouseProvider = async () => {
    try {
      const { data } = await api.get("/admin/data-provider");
      setHouseProvider(data);
    } catch (err) {
      console.error("House data provider error:", err);
    }
  };

  const handleSetHouseProvider = async (name) => {
    setSavingProvider(true);
    try {
      const { data } = await api.put("/admin/data-provider", { name });
      setHouseProvider(data);
    } catch (err) {
      alert(formatApiError(err.response?.data?.detail) || "Could not switch data provider");
    } finally {
      setSavingProvider(false);
    }
  };

  const handleRunScannerRefresh = async () => {
    setRunningRefresh(true);
    try {
      const { data } = await api.post("/admin/scanner-refresh");
      await loadHouseProvider();
      if (data?.skipped) {
        alert("A cache refresh is already running. A second overlapping job was skipped so the 512MB instance does not OOM.");
      } else {
        alert(`Refresh finished: quotes ${data.quotes_ok}/${data.universe}, chains ${data.chain_ok}, enrichment ${data.enrich_ok}`);
      }
    } catch (err) {
      alert(formatApiError(err.response?.data?.detail) || "Scanner refresh failed");
    } finally {
      setRunningRefresh(false);
    }
  };

  const handleRunIntelRefresh = async () => {
    setRunningIntel(true);
    try {
      const { data } = await api.post("/admin/intel-refresh");
      await loadHouseProvider();
      if (data?.skipped) {
        alert("Shadow Money / Ticker Talk cache is already running.");
      } else {
        alert(`Intel cache finished: ${data.ok}/${data.universe} tickers`);
      }
    } catch (err) {
      alert(formatApiError(err.response?.data?.detail) || "Intel refresh failed");
    } finally {
      setRunningIntel(false);
    }
  };

  const loadTrialing = async () => {
    try {
      const { data } = await api.get("/admin/tier/trialing");
      setTrialing(data || { trialing: [], count: 0 });
    } catch (err) {
      console.error("Trialing error:", err);
    }
  };

  const handleExtendTrial = async (uid, email, days) => {
    if (!window.confirm(`Extend ${email}'s trial by ${days} day${days === 1 ? "" : "s"}?`)) return;
    setExtendingUid(uid);
    try {
      await api.post(`/admin/tier/extend/${uid}`, { days });
      loadTrialing();
    } catch (err) {
      console.error("Extend error:", err);
      alert(err.response?.data?.detail || "Extend failed");
    } finally {
      setExtendingUid(null);
    }
  };

  const handleAnnounceLaunch = async () => {
    const eligible = waitlist.waitlist.filter((w) => !w.announced_at).length;
    if (eligible === 0) {
      alert("No eligible waitlist rows — everyone has already been announced to.");
      return;
    }
    if (!window.confirm(`Send the Pro launch email to ${eligible} waitlist member${eligible === 1 ? "" : "s"}? This cannot be undone.`)) return;
    setAnnouncing(true);
    try {
      const { data } = await api.post("/admin/tier/waitlist/announce", { interested_in: "pro_monthly" });
      alert(`Sent: ${data.sent} • Failed: ${data.failed}`);
      loadWaitlist();
    } catch (err) {
      console.error("Announce error:", err);
      alert(err.response?.data?.detail || "Announce failed");
    } finally {
      setAnnouncing(false);
    }
  };

  useEffect(() => { loadUsers(); loadWaitlist(); loadTrialing(); loadClerkWaitlist(); loadHouseProvider(); }, []);

  const handleCreate = async () => {
    try {
      await api.post("/admin/users", form);
      setShowDialog(false);
      setForm({ username: "", email: "", role: "user", password: "" });
      loadUsers();
    } catch (err) {
      console.error("Create user error:", err);
      alert(formatApiError(err.response?.data?.detail) || "Error creating user");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this user? This removes all their data.")) return;
    try {
      await api.delete(`/admin/users/${id}`);
      loadUsers();
    } catch (err) {
      console.error("Delete error:", err);
    }
  };

  const handleResetPassword = async (id) => {
    try {
      await api.put(`/admin/users/${id}/reset-password`);
      loadUsers();
    } catch (err) {
      console.error("Reset error:", err);
    }
  };

  const [pwDialog, setPwDialog] = useState(null); // { userId, email, value }
  const submitPwDialog = async () => {
    if (!pwDialog || pwDialog.value.length < 8) {
      window.alert("Password must be at least 8 characters.");
      return;
    }
    try {
      await api.post(`/admin/users/${pwDialog.userId}/set-password`, { new_password: pwDialog.value });
      setPwDialog(null);
      loadUsers();
      window.alert(`Password set for ${pwDialog.email}.`);
    } catch (err) {
      console.error("Set password error:", err);
      window.alert(formatApiError(err?.response?.data?.detail) || "Failed to set password.");
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-full p-8">
      <RefreshCw className="w-5 h-5 animate-spin" style={{ color: "var(--hoip-text-tertiary)" }} />
    </div>
  );

  return (
    <div className="p-3 md:p-6 space-y-3 md:space-y-4" data-testid="admin-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-emerald-500" />
          <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: "Chivo, sans-serif" }}>Admin Panel</h1>
        </div>
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogTrigger asChild>
            <Button size="sm" className="h-8 bg-white text-black hover:bg-gray-200 rounded-sm text-xs" data-testid="add-user-btn">
              <Plus className="w-3 h-3 mr-1" /> Add User
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#111111] border-[#27272A] text-[#F4F4F5]" data-testid="add-user-dialog" aria-describedby="user-dialog-desc">
            <DialogHeader>
              <DialogTitle className="text-sm" style={{ fontFamily: "Chivo, sans-serif" }}>Add New User</DialogTitle>
            </DialogHeader>
            <p id="user-dialog-desc" className="sr-only">Form to create a new user in Clerk, then provision the MarkovLab profile</p>
            <div className="space-y-3 mt-2">
              <div>
                <Label className="text-xs mb-1 block" style={{ color: "var(--hoip-text-secondary)" }}>Username</Label>
                <Input data-testid="admin-username-input" value={form.username} onChange={(e) => setForm({...form, username: e.target.value})}
                  className="bg-[#0A0A0A] border-[#27272A] rounded-sm h-8 text-xs" />
              </div>
              <div>
                <Label className="text-xs mb-1 block" style={{ color: "var(--hoip-text-secondary)" }}>Email</Label>
                <Input data-testid="admin-email-input" type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})}
                  className="bg-[#0A0A0A] border-[#27272A] rounded-sm h-8 text-xs" />
              </div>
              <div>
                <Label className="text-xs mb-1 block" style={{ color: "var(--hoip-text-secondary)" }}>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({...form, role: v})}>
                  <SelectTrigger className="h-8 bg-[#0A0A0A] border-[#27272A] text-xs rounded-sm" data-testid="admin-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#111111] border-[#27272A]">
                    <SelectItem value="user" className="text-xs text-[#F4F4F5]">User</SelectItem>
                    <SelectItem value="admin" className="text-xs text-[#F4F4F5]">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs mb-1 block" style={{ color: "var(--hoip-text-secondary)" }}>Password (optional — stored in Clerk)</Label>
                <Input data-testid="admin-password-input" type="password" value={form.password}
                  onChange={(e) => setForm({...form, password: e.target.value})}
                  placeholder="Leave blank for Clerk email / Google sign-in"
                  className="bg-[#0A0A0A] border-[#27272A] rounded-sm h-8 text-xs" />
              </div>
              <p className="text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>
                Creates the user in Clerk first so the account appears in Clerk audit logs, then provisions the MarkovLab profile.
              </p>
              <Button onClick={handleCreate} className="w-full bg-white text-black hover:bg-gray-200 rounded-sm h-8 text-xs" data-testid="submit-user-btn">
                Create User
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}
        data-testid="house-data-provider-card">
        <div className="p-4 border-b flex items-center justify-between gap-2 flex-wrap" style={{ borderColor: "var(--hoip-border)" }}>
          <div className="flex items-center gap-2">
            <Database className="w-4 h-4" style={{ color: "#34D399" }} />
            <span className="text-xs font-medium" style={{ color: "var(--hoip-text-secondary)" }}>
              House data provider
            </span>
          </div>
          <button onClick={loadHouseProvider}
            className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-mono-data border rounded-sm hover:bg-[#1A1A1A]"
            style={{ borderColor: "var(--hoip-border)", color: "var(--hoip-text-tertiary)" }}>
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
        </div>
        <div className="p-4 space-y-3">
          <p className="text-[11px]" style={{ color: "var(--hoip-text-tertiary)" }}>
            Users without a linked broker see this feed. Scanner and Wheel Income read the Mongo cache; the key lives on the backend only as FINANCIALDATA_NET_API_KEY.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            {["yfinance", "financialdata_net"].map((name) => (
              <button key={name} disabled={savingProvider}
                onClick={() => handleSetHouseProvider(name)}
                data-testid={`house-provider-${name}`}
                className="px-2 py-1 text-[11px] font-mono-data border rounded-sm"
                style={{
                  borderColor: houseProvider?.name === name ? "#34D399" : "var(--hoip-border)",
                  color: houseProvider?.name === name ? "#34D399" : "var(--hoip-text-secondary)",
                  background: houseProvider?.name === name ? "rgba(52,211,153,0.08)" : "transparent",
                }}>
                {name}
              </button>
            ))}
          </div>
          {houseProvider?.name === "financialdata_net" && !houseProvider?.key_configured && (
            <p className="text-[11px] text-amber-400">
              FINANCIALDATA_NET_API_KEY is not set on the backend. Quotes and chains will fall back to yfinance until you add it in Render.
            </p>
          )}
          <div className="text-[10px] font-mono-data" style={{ color: "var(--hoip-text-tertiary)" }}>
            Active house feed: {houseProvider?.live_provider || "—"}
            {houseProvider?.last_refresh?.in_progress ? " · quote job running" : ""}
          </div>
          <CacheCoverageDashboard houseProvider={houseProvider} onReloadProvider={loadHouseProvider} />
        </div>
      </div>

      {/* Clerk waitlist — beta Approvals (separate from Pro-tier waitlist below) */}
      <div className="border" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}
        data-testid="clerk-approvals-panel">
        <div className="p-4 border-b flex items-center justify-between gap-2 flex-wrap" style={{ borderColor: "var(--hoip-border)" }}>
          <div className="flex items-center gap-2">
            <UserCheck className="w-4 h-4" style={{ color: "#34D399" }} />
            <span className="text-xs font-medium" style={{ color: "var(--hoip-text-secondary)" }}>
              Approvals — Clerk waitlist · {clerkWaitlist.count}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={loadClerkWaitlist}
              className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-mono-data border rounded-sm hover:bg-[#1A1A1A]"
              style={{ borderColor: "var(--hoip-border)", color: "var(--hoip-text-tertiary)" }}
              data-testid="clerk-waitlist-refresh">
              <RefreshCw className="w-3 h-3" /> Refresh
            </button>
            <a
              href={clerkWaitlist.clerk_dashboard_url || "https://dashboard.clerk.com"}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono-data border rounded-sm hover:bg-emerald-900/20"
              style={{ borderColor: "#065F46", color: "#34D399" }}
              data-testid="clerk-dashboard-link"
            >
              <ExternalLink className="w-3 h-3" />
              Open in Clerk
            </a>
          </div>
        </div>
        {clerkWaitlist.error && (
          <div className="px-4 py-2 text-[11px] border-b" style={{ borderColor: "var(--hoip-border)", color: "#F87171" }}
            data-testid="clerk-waitlist-error">
            Could not load waitlist from Clerk ({String(clerkWaitlist.error)}). Vet in the Clerk Dashboard (Users → Waitlist).
          </div>
        )}
        {clerkWaitlist.entries.length === 0 ? (
          <div className="p-4 text-xs" style={{ color: "var(--hoip-text-tertiary)" }}>
            No Clerk waitlist entries. Applicants who join via Sign up appear here. Invite sends Clerk&apos;s Invitation email (rebrand copy in Clerk → Emails).
          </div>
        ) : (
          <table className="w-full dense-table" data-testid="clerk-waitlist-table">
            <thead>
              <tr className="border-b" style={{ borderColor: "var(--hoip-border)" }}>
                <th className="text-left text-xs font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Email</th>
                <th className="text-left text-xs font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Status</th>
                <th className="text-left text-xs font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Requested</th>
                <th className="text-right text-xs font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {clerkWaitlist.entries.map((e) => (
                <tr key={e.id} className="border-b hover:bg-[#1A1A1A]"
                  style={{ borderColor: "var(--hoip-divider)" }}
                  data-testid={`clerk-waitlist-row-${e.email_address}`}>
                  <td className="text-xs font-mono-data" style={{ color: "var(--hoip-text-secondary)" }}>{e.email_address}</td>
                  <td>
                    <span className={`text-[10px] px-1.5 py-0.5 border font-mono-data rounded-sm ${
                      e.status === "pending" ? "text-amber-400 border-amber-800"
                        : e.status === "invited" ? "text-emerald-500 border-emerald-800"
                        : e.status === "completed" ? "text-[#A1A1AA] border-[#27272A]"
                        : "text-red-400 border-red-800"
                    }`}>
                      {(e.status || "unknown").toUpperCase()}
                    </span>
                  </td>
                  <td className="text-xs font-mono-data" style={{ color: "var(--hoip-text-tertiary)" }}>
                    {e.created_at ? new Date(e.created_at).toLocaleString() : "—"}
                  </td>
                  <td className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      {e.status === "pending" && !e.is_locked && (
                        <button
                          onClick={() => handleClerkInvite(e.id, e.email_address)}
                          disabled={clerkActingId === e.id}
                          className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono-data border border-emerald-800 text-emerald-500 hover:bg-emerald-900/30 rounded-sm disabled:opacity-40"
                          data-testid={`clerk-invite-${e.email_address}`}
                        >
                          Invite
                        </button>
                      )}
                      {(e.status === "pending" || e.status === "invited") && !e.is_locked && (
                        <button
                          onClick={() => handleClerkReject(e.id, e.email_address)}
                          disabled={clerkActingId === e.id}
                          className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono-data border border-red-800 text-red-400 hover:bg-red-900/30 rounded-sm disabled:opacity-40"
                          data-testid={`clerk-reject-${e.email_address}`}
                        >
                          <X className="w-3 h-3" />
                          Deny
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Iter166 — Trialing users (extend +7 / +14 days) */}
      <div className="border" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}
        data-testid="trialing-panel">
        <div className="p-4 border-b flex items-center gap-2" style={{ borderColor: "var(--hoip-border)" }}>
          <Clock className="w-4 h-4" style={{ color: "#F59E0B" }} />
          <span className="text-xs font-medium" style={{ color: "var(--hoip-text-secondary)" }}>
            Active trials — {trialing.count}
          </span>
        </div>
        {trialing.count === 0 ? (
          <div className="p-4 text-xs" style={{ color: "var(--hoip-text-tertiary)" }}>
            No users currently on trial. Invited Clerk users appear here after first sign-in.
          </div>
        ) : (
          <table className="w-full dense-table" data-testid="trialing-table">
            <thead>
              <tr className="border-b" style={{ borderColor: "var(--hoip-border)" }}>
                <th className="text-left text-xs font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Email</th>
                <th className="text-left text-xs font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Status</th>
                <th className="text-left text-xs font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Started</th>
                <th className="text-left text-xs font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Ends</th>
                <th className="text-left text-xs font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Days left</th>
                <th className="text-right text-xs font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Extend</th>
              </tr>
            </thead>
            <tbody>
              {trialing.trialing.map((t) => (
                <tr key={t.id} className="border-b hover:bg-[#1A1A1A]"
                  style={{ borderColor: "var(--hoip-divider)" }}
                  data-testid={`trialing-row-${t.email}`}>
                  <td className="text-xs font-mono-data" style={{ color: "var(--hoip-text-secondary)" }}>{t.email}</td>
                  <td>
                    <span className={`text-[10px] px-1.5 py-0.5 border font-mono-data rounded-sm ${
                      t.trial_status === "trial_active"
                        ? "text-emerald-500 border-emerald-800"
                        : "text-red-400 border-red-800"
                    }`}>
                      {t.trial_status === "trial_active" ? "ACTIVE" : "PAUSED"}
                    </span>
                  </td>
                  <td className="text-xs font-mono-data" style={{ color: "var(--hoip-text-tertiary)" }}>
                    {t.trial_started_at ? new Date(t.trial_started_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="text-xs font-mono-data" style={{ color: "var(--hoip-text-tertiary)" }}>
                    {t.trial_ends_at ? new Date(t.trial_ends_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="text-xs font-mono-data" style={{
                    color: t.days_left != null && t.days_left <= 1 ? "#F59E0B" : "var(--hoip-text-secondary)",
                  }}>
                    {t.days_left != null ? (t.days_left < 0 ? `${-t.days_left}d ago` : `${t.days_left}d`) : "—"}
                  </td>
                  <td className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => handleExtendTrial(t.id, t.email, 7)}
                        disabled={extendingUid === t.id}
                        className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono-data border border-amber-800 text-amber-400 hover:bg-amber-900/30 rounded-sm disabled:opacity-40"
                        data-testid={`extend-7-${t.email}`}>
                        <CalendarPlus className="w-3 h-3" />
                        +7d
                      </button>
                      <button
                        onClick={() => handleExtendTrial(t.id, t.email, 14)}
                        disabled={extendingUid === t.id}
                        className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono-data border border-[#27272A] text-[#A1A1AA] hover:bg-[#1A1A1A] rounded-sm disabled:opacity-40"
                        data-testid={`extend-14-${t.email}`}>
                        +14d
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Iter164 — Pro tier waitlist */}
      <div className="border" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}
        data-testid="pro-waitlist-panel">
        <div className="p-4 border-b flex items-center justify-between" style={{ borderColor: "var(--hoip-border)" }}>
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4" style={{ color: "#4fc3f7" }} />
            <span className="text-xs font-medium" style={{ color: "var(--hoip-text-secondary)" }}>
              Pro tier waitlist — {waitlist.count} interested
              {waitlist.waitlist.some((w) => !w.announced_at) && (
                <span className="ml-2 text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>
                  ({waitlist.waitlist.filter((w) => !w.announced_at).length} not yet announced to)
                </span>
              )}
            </span>
          </div>
          {waitlist.count > 0 && (
            <button onClick={handleAnnounceLaunch} disabled={announcing}
              className="inline-flex items-center gap-1 px-3 py-1 text-[10px] font-mono-data border rounded-sm hover:bg-cyan-500/10 disabled:opacity-50"
              style={{ borderColor: "#4fc3f7", color: "#4fc3f7" }}
              data-testid="waitlist-announce-btn">
              <Send className="w-3 h-3" />
              {announcing ? "SENDING…" : "SEND LAUNCH EMAIL"}
            </button>
          )}
        </div>
        {waitlist.count === 0 ? (
          <div className="p-4 text-xs" style={{ color: "var(--hoip-text-tertiary)" }}>
            No signups yet. Users who click &quot;Join Pro waitlist&quot; on the pricing page will appear here.
          </div>
        ) : (
          <table className="w-full dense-table" data-testid="waitlist-table">
            <thead>
              <tr className="border-b" style={{ borderColor: "var(--hoip-border)" }}>
                <th className="text-left text-xs font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Email</th>
                <th className="text-left text-xs font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Signed up</th>
                <th className="text-left text-xs font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Note</th>
                <th className="text-left text-xs font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Announced</th>
              </tr>
            </thead>
            <tbody>
              {waitlist.waitlist.map((w) => (
                <tr key={w.id} className="border-b hover:bg-[#1A1A1A]"
                  style={{ borderColor: "var(--hoip-divider)" }}
                  data-testid={`waitlist-row-${w.email}`}>
                  <td className="text-xs font-mono-data" style={{ color: "var(--hoip-text-secondary)" }}>{w.email}</td>
                  <td className="text-xs font-mono-data" style={{ color: "var(--hoip-text-tertiary)" }}>
                    {w.created_at ? new Date(w.created_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="text-xs" style={{ color: "var(--hoip-text-tertiary)" }}>
                    {w.note ? <span title={w.note}>{w.note.slice(0, 60)}{w.note.length > 60 ? "…" : ""}</span> : "—"}
                  </td>
                  <td className="text-xs font-mono-data">
                    {w.announced_at ? (
                      <span className="text-emerald-500 text-[10px]">✓ {new Date(w.announced_at).toLocaleDateString()}</span>
                    ) : w.announce_error ? (
                      <span className="text-red-400 text-[10px]" title={w.announce_error}>failed</span>
                    ) : (
                      <span className="text-[#71717A] text-[10px]">pending</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="border" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}>
        <div className="p-4 border-b flex items-center gap-2" style={{ borderColor: "var(--hoip-border)" }}>
          <Users className="w-4 h-4" style={{ color: "var(--hoip-text-tertiary)" }} />
          <span className="text-xs" style={{ color: "var(--hoip-text-secondary)" }}>{users.length} / 10 users</span>
        </div>
        <table className="w-full dense-table" data-testid="admin-users-table">
          <thead>
            <tr className="border-b" style={{ borderColor: "var(--hoip-border)" }}>
              <th className="text-left text-xs font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Username</th>
              <th className="text-left text-xs font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Email</th>
              <th className="text-left text-xs font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Role</th>
              <th className="text-left text-xs font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Tier</th>
              <th className="text-left text-xs font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Auth</th>
              <th className="text-left text-xs font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Password Set</th>
              <th className="text-right text-xs font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b hover:bg-[#1A1A1A]" style={{ borderColor: "var(--hoip-divider)" }}>
                <td className="font-mono-data text-xs font-medium">{u.username}</td>
                <td className="text-xs" style={{ color: "var(--hoip-text-secondary)" }}>{u.email}</td>
                <td>
                  <span className={`text-xs px-2 py-0.5 border font-mono-data ${
                    u.role === "admin" ? "text-emerald-500 border-emerald-800" : "text-[#A1A1AA] border-[#27272A]"
                  }`}>
                    {u.role}
                  </span>
                </td>
                <td className="text-xs" style={{ color: "var(--hoip-text-secondary)" }}>
                  {u.tier || "—"}
                </td>
                <td>
                  {u.clerk_user_id || u.auth_provider === "clerk" ? (
                    <span className="text-[10px] px-1.5 py-0.5 border border-emerald-800 text-emerald-400 font-mono-data">
                      Clerk
                    </span>
                  ) : u.google_linked ? (
                    <span className="text-[10px] px-1.5 py-0.5 border border-blue-800 text-blue-400 font-mono-data" data-testid={`google-badge-${u.username}`}>
                      Google
                    </span>
                  ) : (
                    <span className="text-[10px] text-[#71717A] font-mono-data">legacy</span>
                  )}
                </td>
                <td className="text-xs">{u.password_set ? <span className="text-emerald-500">Yes</span> : <span className="text-yellow-500">No</span>}</td>
                <td className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <button
                      onClick={() => setPwDialog({ userId: u.id, email: u.email, value: "" })}
                      className="px-2 py-0.5 text-[10px] font-mono-data border border-emerald-800 text-emerald-500 hover:bg-emerald-900/30 rounded-sm"
                      title="Directly set this user's password"
                      data-testid={`set-pw-${u.username}`}
                    >
                      SET PW
                    </button>
                    {u.role !== "admin" && (
                      <button
                        onClick={() => handleResetPassword(u.id)}
                        className="p-1 hover:bg-[#1A1A1A] rounded-sm"
                        title="Reset password (user must set new one at next login)"
                        data-testid={`reset-pw-${u.username}`}
                      >
                        <RotateCcw className="w-3 h-3" style={{ color: "var(--hoip-text-tertiary)" }} />
                      </button>
                    )}
                    {u.role !== "admin" && (
                      <button
                        onClick={() => handleDelete(u.id)}
                        className="p-1 hover:bg-red-900/30 rounded-sm"
                        title="Delete user"
                        data-testid={`delete-user-${u.username}`}
                      >
                        <Trash2 className="w-3 h-3 text-red-500" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <PiiAuditCard />

      <CommunicationsCard />
      <AssistantCohortCard />

      {/* Iter201 — import → portfolio reconciliation backfill */}
      <ImportReconcileCard />

      {/* Set-password dialog */}
      {pwDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
          onClick={() => setPwDialog(null)}
          data-testid="set-pw-dialog">
          <div className="border p-4 rounded-sm w-[420px]"
            style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}
            onClick={(e) => e.stopPropagation()}>
            <div className="text-[11px] font-bold mb-1">SET PASSWORD</div>
            <div className="text-[11px] opacity-70 mb-3">Target user: <span className="font-mono-data">{pwDialog.email}</span></div>
            <label className="text-[10px] opacity-70 block mb-1">New password (min 8 chars)</label>
            <input
              type="text"
              autoFocus
              value={pwDialog.value}
              onChange={(e) => setPwDialog({ ...pwDialog, value: e.target.value })}
              onKeyDown={(e) => e.key === "Enter" && submitPwDialog()}
              className="w-full h-8 px-2 text-[12px] rounded-sm border bg-[#0A0A0A] border-[#27272A] text-[#F4F4F5]"
              data-testid="set-pw-input"
              placeholder="new password"
            />
            <div className="text-[10px] opacity-60 mt-2">
              Saved in Clerk. Share it out-of-band; the user should rotate it in the Clerk account menu.
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setPwDialog(null)}
                className="px-3 py-1 text-[11px] border" style={{ borderColor: "var(--hoip-border)" }}
                data-testid="set-pw-cancel">
                Cancel
              </button>
              <button onClick={submitPwDialog}
                className="px-3 py-1 text-[11px] font-bold"
                style={{ background: "var(--hoip-text-primary)", color: "var(--hoip-bg)" }}
                data-testid="set-pw-submit">
                SET PASSWORD
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
